import { constant } from "fp-ts/lib/function.js";
import { FetchError, makeSupportApi } from "./api.js";
import { match } from "ts-pattern";
import { useAnalytics } from "../google-analytics/index.js";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import qs from "query-string";
import React from "react";
const useAccessCodeState = (key, initialValue) => {
    const [storedValue, setStoredValue] = React.useState(() => {
        if (typeof window === "undefined") {
            return initialValue;
        }
        const parsed = qs.parse(window.location.search);
        const accessCode = parsed.accessCode ? String(parsed.accessCode) : undefined;
        if (accessCode) {
            try {
                localStorage.setItem(key, accessCode);
                return accessCode;
            }
            catch (error) {
                console.log(error);
                return initialValue;
            }
        }
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        }
        catch (error) {
            console.log(error);
            return initialValue;
        }
    });
    const setValue = (value) => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            if (typeof window !== "undefined") {
                window.localStorage.setItem(key, JSON.stringify(valueToStore));
            }
        }
        catch (error) {
            console.log(error);
        }
    };
    return [storedValue, setValue];
};
export const SupportContext = React.createContext({
    isLoading: false,
    data: undefined,
    error: undefined,
});
SupportContext.displayName = "SupportContext";
export const SupportProvider = ({ api = makeSupportApi(), children, enableQrScanner, localStorageKey = "culinario-mortale-access-code", }) => {
    const { t } = useTranslation("c-support");
    const [accessCode, setAccessCode] = useAccessCodeState(localStorageKey, "");
    const [usedMethod, setUsedMethod] = React.useState();
    const [enabled, setEnabled] = React.useState(false);
    const trackEvent = useAnalytics();
    const parseError = (e) => {
        const code = e && e instanceof FetchError ? e.status : 500;
        const message = match(code)
            .with(403, constant(t("feedback-invalid-code", "Dein Zugangscode ist nicht gültig. Bitte überprüfe die Schreibweise und versuche es erneut.")))
            .otherwise(constant(t("feedback-technical-error", "Leider ist ein technischer Fehler aufgetreten. Bitte kontaktiere uns mit deinem Zugangscode unter support@culinario-mortale.de und wir werden dir weiterhelfen.")));
        return { code, message };
    };
    const { data, error, isFetched, isFetching: isLoading, refetch, } = useQuery([accessCode], async ({ signal }) => api.getGame({ accessCode, signal }), {
        enabled: enabled && !!accessCode && accessCode.length > 0,
        keepPreviousData: false,
        refetchOnWindowFocus: false,
        retry: 1,
        retryDelay: 300,
        retryOnMount: false,
        onError: () => {
            setAccessCode("");
            trackEvent("exception", {
                description: "Support login failed",
                fatal: false,
            });
        },
        onSuccess() {
            if (!usedMethod)
                return;
            trackEvent("login", { method: usedMethod });
        },
    });
    React.useEffect(() => {
        setEnabled(true);
    }, []);
    const downloadFullBooklet = async (bookletId) => {
        const downloadLink = await api.requestFullBookletUrl({ accessCode, bookletId });
        await refetch();
        return downloadLink;
    };
    const submitAccessCode = (code, method) => {
        setAccessCode(code);
        setUsedMethod(method);
        isFetched && refetch();
    };
    const reset = () => {
        setAccessCode("");
    };
    return (React.createElement(SupportContext.Provider, { value: {
            accessCode,
            data,
            downloadFullBooklet,
            enableQrScanner,
            error: !!error ? parseError(error) : undefined,
            isLoading,
            reset,
            submitAccessCode,
        } }, children));
};
//# sourceMappingURL=provider.js.map